import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UsageService from "../services/usage";
import { FILTER_VALUES } from "@/constants/filters";
import { queryKeys } from "@/constants/queryKeys";
import { USAGE_VIEW_TYPES } from "@/constants/usage";
import { useCustomerScopedQuery } from "@/hooks/useCustomerScopedQuery";
import { services } from "@/services/registry";
/**  fetching usage data based on filters */ export const useUsageData = (filters, contractId)=>{
    const { viewType, selectedDate, startDate, endDate, selectedApiKey } = filters;
    // For hourly view - single day data
    const hourlyQuery = useCustomerScopedQuery({
        queryKey: queryKeys.usage.chartHourly(contractId || undefined, selectedDate, selectedApiKey, undefined, undefined),
        queryFn: ({ customerId })=>services.usageService.getHourlyUsage(contractId, selectedDate, selectedApiKey === FILTER_VALUES.ALL ? undefined : selectedApiKey, customerId),
        enabled: viewType === USAGE_VIEW_TYPES.HOURLY && !!contractId && !!selectedDate,
        staleTime: 5 * 60 * 1000
    });
    // For daily view - date range data
    const dailyQuery = useCustomerScopedQuery({
        queryKey: queryKeys.usage.chartDaily(contractId || undefined, startDate, endDate, selectedApiKey, undefined, undefined),
        queryFn: ({ customerId })=>services.usageService.getDailyAggregatedUsage(contractId, startDate, endDate, selectedApiKey === FILTER_VALUES.ALL ? undefined : selectedApiKey, customerId),
        enabled: viewType === USAGE_VIEW_TYPES.DAILY && !!contractId && !!startDate && !!endDate,
        staleTime: 5 * 60 * 1000
    });
    const activeQuery = viewType === USAGE_VIEW_TYPES.HOURLY ? hourlyQuery : dailyQuery;
    if (!contractId) {
        return {
            ...activeQuery,
            error: new Error("No contracts found. Please ensure you have active contracts to view usage data.")
        };
    }
    return activeQuery;
};
/**
 * Hook for fetching detailed metered usage with API key breakdowns
 */ export const useMeteredUsage = (contractId, startDate, endDate, selectedApiKey = FILTER_VALUES.ALL)=>{
    const query = useCustomerScopedQuery({
        queryKey: queryKeys.usage.chartMetered(contractId || undefined, startDate, endDate, selectedApiKey),
        queryFn: ({ customerId })=>services.usageService.getMeteredUsage(contractId, startDate, endDate, selectedApiKey === FILTER_VALUES.ALL ? undefined : selectedApiKey, customerId),
        enabled: !!contractId && !!startDate && !!endDate,
        staleTime: 5 * 60 * 1000
    });
    if (!contractId) {
        return {
            ...query,
            error: new Error("No contracts found for metered usage data.")
        };
    }
    return query;
};
/**
 * Mutation hook for creating metered usage records
 */ export const useCreateMeteredUsage = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ apiKeyId, data })=>services.usageService.createMeteredUsage(apiKeyId, data),
        onSuccess: ()=>{
            // Invalidate all usage queries to refresh data
            void queryClient.invalidateQueries({
                queryKey: queryKeys.usage.all()
            });
        }
    });
};
/**
 * Hook for manually refetching usage data
 */ export const useRefreshUsageData = ()=>{
    const queryClient = useQueryClient();
    return useCallback(()=>{
        void queryClient.invalidateQueries({
            queryKey: queryKeys.usage.all()
        });
    }, [
        queryClient
    ]);
};
export default UsageService;
